import { motion } from "framer-motion";
import {
  AlertTriangle,
  CalendarCheck,
  CalendarX,
  Mail,
  Receipt,
  RefreshCcw,
} from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import {
  useEnviarEmailImileFechamento,
  useImileFechamentoStatus,
} from "./hooks/use-imile-fechamento";
import { DiaFaltante } from "./types";

/**
 * Fechamento da iMile: só o status da quinzena e o disparo de e-mails.
 *
 * O período é a QUINZENA, não a semana: 1 a 15 e 16 ao último dia do mês, a mesma
 * divisão que data_vencimento_imile usa para somar os 40 dias.
 *
 * É a tela de controle-nfs do entrego reduzida — sem as abas, sem saldo
 * remanescente, sem NFS, sem ZIPs e sem o painel de completude de notas.
 * O bloco "Quinzena Completa / Incompleta" fica: é *o* fechamento, e sem ele a
 * chamada de verificação não mostraria nada.
 */
export default function ImileFechamentoPage() {
  const [confirmEmailFechamento, setConfirmEmailFechamento] = useState(false);
  const [enviarParaContato, setEnviarParaContato] = useState(false);

  const {
    data: fechamentoStatus,
    isLoading: loadingFechamento,
    refetch: refetchFechamento,
  } = useImileFechamentoStatus();

  const enviarEmailFechamentoMutation = useEnviarEmailImileFechamento();

  const handleConfirmEmailFechamento = () => {
    enviarEmailFechamentoMutation.mutate(
      { enviar_email: true, enviar_para_contato: enviarParaContato },
      { onSuccess: () => setConfirmEmailFechamento(false) }
    );
  };

  const formatDatePTBR = (dateStr?: string) => {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const renderFechamentoStatus = () => {
    if (loadingFechamento && !fechamentoStatus) {
      return (
        <Card className="mb-6 flex justify-center items-center py-12">
          <RefreshCcw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">
            Verificando status da quinzena...
          </span>
        </Card>
      );
    }

    if (!fechamentoStatus) {
      return (
        <Card className="mb-6 flex justify-center py-6">
          <Button variant="outline" onClick={() => refetchFechamento()}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Verificar Status
          </Button>
        </Card>
      );
    }

    const { quinzena_completa, periodo, dias_faltantes } = fechamentoStatus;
    // O label vem pronto do backend ("1ª quinzena de setembro/2026"); as datas
    // entram junto porque é por elas que se confere o romaneio.
    const periodoStr = periodo
      ? `${periodo.label} · ${formatDatePTBR(periodo.inicio)} – ${formatDatePTBR(periodo.fim)}`
      : "";

    if (quinzena_completa) {
      return (
        <Card className="mb-6 border-green-500 bg-green-50/50 dark:bg-green-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <CalendarCheck className="h-10 w-10 text-green-600 dark:text-green-500" />
                <div>
                  <h3 className="text-xl font-bold text-green-700 dark:text-green-400">
                    Quinzena Completa
                  </h3>
                  {periodoStr && (
                    <p className="text-sm text-muted-foreground">{periodoStr}</p>
                  )}
                </div>
              </div>
              <Badge
                variant="outline"
                className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-200"
              >
                ✓ Fechado
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Todos os dias da quinzena possuem registros comprados ou
              liquidados para todas as bases cadastradas.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mb-6 border-amber-500 bg-amber-50/50 dark:bg-amber-950/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CalendarX className="h-10 w-10 text-amber-600 dark:text-amber-500" />
              <div>
                <h3 className="text-xl font-bold text-amber-700 dark:text-amber-400">
                  Quinzena Incompleta
                </h3>
                {periodoStr && (
                  <p className="text-sm text-muted-foreground">{periodoStr}</p>
                )}
              </div>
            </div>
            <Badge
              variant="outline"
              className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 border-amber-200"
            >
              Pendente
            </Badge>
          </div>

          {dias_faltantes && dias_faltantes.length > 0 && (
            <div className="mt-4 border rounded-md overflow-hidden bg-background">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-amber-700 dark:text-amber-400 w-48">
                      Data Romaneio
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-amber-700 dark:text-amber-400">
                      Bases faltantes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(
                    dias_faltantes.reduce<Record<string, string[]>>(
                      (acc, item: DiaFaltante) => {
                        if (!acc[item.date]) acc[item.date] = [];
                        acc[item.date].push(item.ops);
                        return acc;
                      },
                      {}
                    )
                  )
                    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                    .map(([date, opsDoDia]) => (
                      <tr key={date}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2 font-medium">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            {formatDatePTBR(date)}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {opsDoDia.map((op) => (
                              <Badge
                                key={op}
                                variant="outline"
                                className="text-xs border-amber-200 text-amber-700 dark:text-amber-400 font-mono"
                              >
                                {op}
                              </Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <p className="border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
                Enquanto houver dia faltante o backend não envia os e-mails.
                Resolva o checkout ou a baixa dessas datas e verifique de novo.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6">
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center gap-3">
          <motion.div
            className="p-2 rounded-lg bg-primary/10"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <Receipt className="h-6 w-6 text-primary" />
          </motion.div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Fechamento iMile
            </h1>
            <p className="text-muted-foreground mt-1">
              Fechamento quinzenal financeiro e disparo dos e-mails por franquia
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {renderFechamentoStatus()}

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Ações de Fechamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => refetchFechamento()}
                disabled={loadingFechamento}
              >
                <RefreshCcw
                  className={`mr-2 h-4 w-4 ${
                    loadingFechamento ? "animate-spin" : ""
                  }`}
                />
                {loadingFechamento
                  ? "Verificando..."
                  : "Verificar Status da Quinzena"}
              </Button>

              <Button
                className="w-full h-12"
                onClick={() => setConfirmEmailFechamento(true)}
                disabled={
                  enviarEmailFechamentoMutation.isPending || loadingFechamento
                }
              >
                {enviarEmailFechamentoMutation.isPending ? (
                  <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                {enviarEmailFechamentoMutation.isPending
                  ? "Enviando..."
                  : "Enviar Email de Fechamento"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <ConfirmDialog
        open={confirmEmailFechamento}
        onOpenChange={setConfirmEmailFechamento}
        onConfirm={handleConfirmEmailFechamento}
        title="Confirmar Envio de Email — Fechamento Quinzenal"
        confirmText={
          enviarEmailFechamentoMutation.isPending
            ? "Enviando..."
            : "Confirmar Envio"
        }
        cancelText="Cancelar"
      >
        <div className="space-y-4">
          <p className="text-sm">
            Você está prestes a enviar o email de fechamento financeiro
            quinzenal da iMile. É <strong>um e-mail por franquia</strong>,
            cobrindo todas as bases dela.
          </p>
          <div className="bg-muted p-3 rounded-lg text-sm space-y-2 border">
            <div>
              <strong>Período:</strong>{" "}
              {fechamentoStatus?.periodo?.label ?? "última quinzena fechada"}
            </div>
            <div>
              <strong>Destinatários:</strong>{" "}
              {enviarParaContato
                ? "Contato externo da franquia + backoffice (CC)"
                : "Apenas backoffice interno"}
            </div>
          </div>
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="imile-enviar-contato"
              checked={enviarParaContato}
              onCheckedChange={setEnviarParaContato}
            />
            <Label htmlFor="imile-enviar-contato">
              Enviar também para contato externo da franquia
            </Label>
          </div>
          <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              Fora de produção o e-mail vai só para o backoffice, mesmo com a
              opção acima ligada.
            </span>
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
